import fallbackImage from "@assets/images/fallback-game.svg";
import {IconHeart, IconHeartFilled, IconPlayerPlay} from "@tabler/icons-react";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Carousel} from "@/components";
import {ImageWithLoadSpinner} from "@/components/image-with-load-spinner";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./MiniGamesCarousel.module.css";

const CAROUSEL_ITEMS = 20;

const MiniGamesCarousel = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const allGames = useMiniGames(state => state.list);
  const isLoading = useMiniGames(state => state.loading);
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();
  const [carouselApi, setCarouselApi] = useState<{scrollPrev: () => void; scrollNext: () => void} | null>(null);

  const games = useMemo(() => allGames.filter(g => g.class === "slot" || g.class === "table").slice(0, 40), [allGames]);

  const handleGameClick = useCallback(
    (provider: string, gameId: string) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleFavorite = useCallback(
    (e: React.MouseEvent, game: {id: string; provider: string}) => {
      e.stopPropagation();
      if (authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      setFavoriteGames(
        {id: game.id, provider: game.provider, game_provider: game.provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [authStatus, handleShowAuthModal, setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  if (!isLoading && games.length === 0) return null;

  return (
    <section
      className={styles.section}
      aria-label={t("miniGamesCarouselAria")}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span
            className={styles.icon}
            aria-hidden="true"
          >
            🎰
          </span>
          <h2 className={styles.title}>{t("miniGamesTitle")}</h2>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.viewAll}
            onClick={() => {
              if (authStatus !== "authenticated") {
                handleShowAuthModal();
                return;
              }
              navigate("/mini-juegos");
            }}
          >
            {t("miniGamesViewAll")}
            <span className={styles.viewAllArrow}>›</span>
          </button>
          {carouselApi && (
            <div className={styles.arrowGroup}>
              <button
                type="button"
                className={styles.arrowBtn}
                onClick={carouselApi.scrollPrev}
                aria-label="Previous"
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className={styles.arrowBtn}
                onClick={carouselApi.scrollNext}
                aria-label="Next"
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading && games.length === 0 ? (
        <div className={styles.skeletonRow}>
          {["s1", "s2", "s3", "s4", "s5", "s6"].map(id => (
            <div
              key={id}
              className={styles.skeletonCard}
            />
          ))}
        </div>
      ) : (
        <Carousel
          onInit={setCarouselApi}
          speed={1.5}
          pauseOnHover
        >
          {Array.from({length: CAROUSEL_ITEMS}).map((_, idx) => {
            const game = games[idx % games.length];
            const isFav = storeFavoriteGames.some((f: {game_id: string}) => f.game_id === game.id);
            return (
              <div
                key={`mg-${game.id}-${idx}`}
                className={styles.card}
                onClick={() => handleGameClick(game.provider, game.id)}
                role="button"
                tabIndex={0}
                aria-label={game.name}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleGameClick(game.provider, game.id);
                  }
                }}
              >
                <ImageWithLoadSpinner
                  src={game.icon || fallbackImage}
                  alt={game.name}
                  fallbackSrc={fallbackImage}
                  wrapperClassName={styles.cardImg}
                  imgClassName={styles.thumbImg}
                  objectFit="cover"
                  loading="lazy"
                  showSpinner={false}
                />
                <div className={styles.gradientOverlay} />
                <div className={styles.playOverlay}>
                  <div className={styles.playBtn}>
                    <IconPlayerPlay
                      size={16}
                      fill="#000"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className={`${styles.favBtn} ${isFav ? styles.favActive : ""}`}
                  onClick={(e: React.MouseEvent) => handleFavorite(e, game)}
                  aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                >
                  {isFav ? (
                    <IconHeartFilled
                      size={12}
                      color="#ff4d6a"
                    />
                  ) : (
                    <IconHeart
                      size={12}
                      stroke={2}
                      color="#fff"
                    />
                  )}
                </button>
                <div className={styles.cardFooter}>
                  <span className={styles.gameName}>{game.name}</span>
                  <span className={styles.gameProvider}>{game.provider}</span>
                </div>
              </div>
            );
          })}
        </Carousel>
      )}
    </section>
  );
};

export default MiniGamesCarousel;
